import { Component, OnInit, Inject } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AnnualSalesService } from '../../services/annual-sales.service';
import { AnnualSalesData, ObjectivesData } from '../../interfaces';
import { AnnualSalesObjectivesComponent } from 'src/app/shared/annual-sales-objectives/annual-sales-objectives.component';
import { ToastrService } from '../ui/toastr/toastr.service';
import { UtilService } from 'src/app/services/util.service';

@Component({
  selector: 'app-annual-sales',
  templateUrl: './annual-sales.component.html',
  styleUrls: ['./annual-sales.component.scss']
})
export class AnnualSalesComponent implements OnInit {
  destroyed$: Subject<boolean> = new Subject<boolean>();

  public chartInstance: any;
  public isLoading = true;

  public annualSalesChartOptions: any;
  public data: AnnualSalesData = null;

  public objectivesData: ObjectivesData = null;

  public yearValues: number[];
  public yearIndex: number;
  public yearIndexMin = false;
  public yearIndexMax = false;
  public year: number;

  private months = [
    'JANUARY',
    'FEBRUARY',
    'MARCH',
    'APRIL',
    'MAY',
    'JUNE',
    'JULY',
    'AUGUST',
    'SEPTEMBER',
    'OCTOBER',
    'NOVEMBER',
    'DECEMBER'
  ];
  monthSelected: 0;

  constructor(
    private annualSales: AnnualSalesService,
    private objectives: AnnualSalesService,
    @Inject(I18NEXT_SERVICE) private i18NextService: ITranslationService,
    private dialog: MatDialog,
    private toastrService: ToastrService,
    private utilService: UtilService
  ) { }

  ngOnInit(): void {
    this.getAnualSales();
    this.i18NextService.events.languageChanged.subscribe(() => {
      if (this.data && this.objectivesData) {
        this.setOptions();
      }
    });
  }

  private getAnualSales(): void {
    this.isLoading = true;
    this.annualSales.getData().pipe(takeUntil(this.destroyed$)).subscribe(res => {
      this.data = res.data;
      if (this.data.years.length > 0) {
        this.yearValues = this.data.years.map(item => item.year);
        this.yearIndex =
          this.yearValues.findIndex(item => item === new Date().getFullYear()) ||
          this.yearValues[this.yearValues.length - 1];
        this.yearIndexMin = this.yearIndex <= 0;
        this.yearIndexMax = this.yearIndex >= this.yearValues.length - 1;
        this.year = this.yearValues[this.yearIndex];
        this.getObjetivesData();
      } else {
        this.isLoading = false;
      }
    },
      error => {
        this.utilService.toastErrors(error);
        this.isLoading = false;
      }
    );
  }

  private getObjetivesData(): void {
    this.objectives.getObjectivesData().pipe(takeUntil(this.destroyed$)).subscribe(res => {
      this.objectivesData = res.data;
      if (this.objectivesData.consumption && this.objectivesData.margin) {
        this.setOptions();
      }
      this.isLoading = false;
    },
      error => {
        this.utilService.toastErrors(error);
        this.isLoading = false;
      });
  }

  openObjectivesDialog() {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      disableClose: true,
      autoFocus: true,
      panelClass: ['modal__full-screen'],
      data: { objConsumption: this.objectivesData.consumption, objMargin: this.objectivesData.margin }
    };

    const dialogRef = this.dialog.open(AnnualSalesObjectivesComponent, dialogConfig.data);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const consumption = (Number.isInteger(+result.objConsumption) && +result.objConsumption > 0)
          ? +result.objConsumption
          : this.objectivesData.consumption;
        const margin = (Number.isInteger(+result.objMargin) && +result.objMargin > 0) ? +result.objMargin : this.objectivesData.margin;

        this.annualSales.setObjectivesData({ consumption, margin }).subscribe(res => {
          this.objectivesData = res.data;
          this.setOptions();
        },
          error => {
            this.utilService.toastErrors(error);
          });
      }
    });
  }

  onChartEvent(event: any, type: string) { }

  onChartInit(e: any) {
    this.chartInstance = e;
    this.chartInstance.on('click', { targetType: 'axisLabel', value: 'category' }, params => {
      if (params.targetType === 'axisLabel') {
        this.chartInstance.dispatchAction({
          type: 'showTip',
          seriesIndex: 0,
        });
      }
    });
    this.chartInstance.on('legendselectchanged', params => {
      switch (params.name) {
        case this.i18NextService.t('ANNUAL_SALES_CHART.CONSUMPTION'):
          this.chartInstance.dispatchAction({
            type: params.selected[this.i18NextService.t('ANNUAL_SALES_CHART.CONSUMPTION')]
              ? 'legendSelect'
              : 'legendUnSelect',
            name: this.i18NextService.t('ANNUAL_SALES_CHART.OBJ_CONSUMPTION')
          });
          break;
        case this.i18NextService.t('ANNUAL_SALES_CHART.MARGIN'):
          this.chartInstance.dispatchAction({
            type: params.selected[this.i18NextService.t('ANNUAL_SALES_CHART.MARGIN')]
              ? 'legendSelect'
              : 'legendUnSelect',
            name: this.i18NextService.t('ANNUAL_SALES_CHART.OBJ_MARGIN')
          });
          break;
        case this.i18NextService.t('ANNUAL_SALES_CHART.OBJ_CONSUMPTION'):
          if (!params.selected[this.i18NextService.t('ANNUAL_SALES_CHART.CONSUMPTION')]) {
            this.chartInstance.dispatchAction({
              type: 'legendUnSelect',
              name: this.i18NextService.t('ANNUAL_SALES_CHART.OBJ_CONSUMPTION')
            });
          }
          break;
        case this.i18NextService.t('ANNUAL_SALES_CHART.OBJ_MARGIN'):
          if (!params.selected[this.i18NextService.t('ANNUAL_SALES_CHART.MARGIN')]) {
            this.chartInstance.dispatchAction({
              type: 'legendUnSelect',
              name: this.i18NextService.t('ANNUAL_SALES_CHART.OBJ_MARGIN')
            });
          }
          break;
        default:
          break;
      }
    });
  }

  public changeYear(dir: 'prev' | 'next') {
    if (dir === 'prev') {
      if (this.yearIndex > 0) {
        this.yearIndex--;
      }
    } else {
      if (this.yearIndex < this.yearValues.length - 1) {
        this.yearIndex++;
      }
    }
    this.yearIndexMin = this.yearIndex <= 0;
    this.yearIndexMax = this.yearIndex >= this.yearValues.length - 1;
    this.year = this.yearValues[this.yearIndex];
    this.setOptions();
  }

  setOptions() {
    const data = this.data.years.filter(item => item.year === this.year);
    const maxConsumo = data[0].months.reduce((prev, current) =>
      prev.datos.consumo > current.datos.consumo ? prev : current
    ).datos.consumo;

    const maxMargen = data[0].months.reduce((prev, current) =>
      prev.datos.margen > current.datos.margen ? prev : current
    ).datos.margen;

    const maxObjConsumption = this.objectivesData?.consumption ? +this.objectivesData.consumption : 0;
    const maxObjMargin = this.objectivesData?.margin ? +this.objectivesData.margin : 0;
    const maxYaxis0 = Math.round((Math.max(maxConsumo, maxObjConsumption) + 5) / 10) * 10;
    const maxYaxis1 = Math.round((Math.max(maxMargen, maxObjMargin) + 5) / 10) * 10;

    const dataObjConsumption = this.objectivesData?.consumption
      ? {
        name: this.i18NextService.t('ANNUAL_SALES_CHART.OBJ_CONSUMPTION'),
        type: 'line',
        yAxisIndex: 0,
        showSymbol: false,
        lineStyle: {
          color: '#1A73E8'
        },
        itemStyle: {
          color: '#1A73E8',
          width: 2
        },
        data: Array(12).fill(this.objectivesData.consumption)
      }
      : null;

    const dataObjMargin = this.objectivesData?.margin
      ? {
        name: this.i18NextService.t('ANNUAL_SALES_CHART.OBJ_MARGIN'),
        type: 'line',
        yAxisIndex: 1,
        showSymbol: false,
        lineStyle: {
          color: '#FF0000'
        },
        itemStyle: {
          color: '#FF0000',
          width: 2
        },
        data: Array(12).fill(this.objectivesData.margin)
      }
      : null;

    this.annualSalesChartOptions = {
      legend: {
        data: [
          this.i18NextService.t('ANNUAL_SALES_CHART.CONSUMPTION'),
          this.i18NextService.t('ANNUAL_SALES_CHART.MARGIN'),
          this.objectivesData?.consumption ? {
            name: this.i18NextService.t('ANNUAL_SALES_CHART.OBJ_CONSUMPTION'),
            icon: 'line'
          } : null,
          this.objectivesData?.margin ? {
            name: this.i18NextService.t('ANNUAL_SALES_CHART.OBJ_MARGIN'),
            icon: 'line'
          } : null
        ],
        left: '0%',
        textStyle: {
          fontSize: '15',
          fontFamily: 'Open Sans Light',
          fontWeight: 'bolder'
        },
        itemGap: 20,
        borderRadius: 5,
        shadowColor: 'rgba(0, 0, 0, 0.5)',
        shadowBlur: 10
      },
      grid: {
        left: '2%',
        right: '2%',
        bottom: '3%',
        containLabel: true
      },
      tooltip: {
        show: true,
        axisPointer: {
          type: 'line'
        },
        trigger: 'axis',
        triggerOn: 'click',
        padding: [2, 8],
        borderWidth: 2,
        borderColor: '#ccc',
        formatter: params => {
          const icon0 = `<span style="border-left: 2px solid #fff;display: inline-block;height: 12px;width: 16px; margin-right: 2px"><span style="background-color:gray;display: block;height: 2px;margin-top: 7px;width: 16px;"></span></span>`;
          const icon1 = `<span style="display: inline-block; width: 16px; height: 2px; background-color: gray; position: relative; border-left: 2px solid #fff; top: -2px; margin-right: 2px"><span style="width: 4px; height: 4px; color: gray; border: 2px solid gray; border-radius: 50%; position: absolute; left: 4px; top: -3px; background-color: white"></span></span>`;
          let consumption = '';
          let consumptionObj = '';
          let margin = '';
          let marginObj = '';

          params.map(param => {
            consumption =
              param.seriesIndex === 0 && param.value
                ? `<span style="color:#1A73E8">${param.value % 1 != 0 ? param.value.toFixed(3).replace('.', ',') : param.value} MWh</span>`
                : consumption;
            consumptionObj =
              param.seriesIndex === 2 && param.value
                ? `<span style="color:#1A73E8">${param.value % 1 != 0 ? param.value.toFixed(3).replace('.', ',') : param.value} MWh</span>`
                : consumptionObj;
            margin =
              param.seriesIndex === 1 && param.value
                ? `<span style="color:#FF0000">${param.value % 1 != 0 ? param.value.toFixed(3).replace('.', ',') : param.value} €/MWh</span>`
                : margin;
            marginObj =
              param.seriesIndex === 3 && param.value
                ? `<span style="color:#FF0000">${param.value % 1 != 0 ? param.value.toFixed(3).replace('.', ',') : param.value} €/MWh</span>`
                : marginObj;
          });

          return `<div style="font-family:Open Sans Bold;font-size:10px">
                    <div style="font-family:Open Sans Regular">${params[0].name}</div>
                    ${(consumption || margin) &&
            `<div>${icon1} ${consumption} ${consumption && margin && ' | '} ${margin}</div>`}
                    ${(consumptionObj || marginObj) &&
            `<div>${icon0} ${consumptionObj} ${consumptionObj && marginObj && ' | '} ${marginObj}</div>`}
                  </div>`;
        }
      },
      xAxis: {
        type: 'category',
        data: this.months.map(item => this.i18NextService.t(`ANNUAL_SALES_CHART.${item}`)),
        axisLine: {
          lineStyle: {
            color: '#DDD'
          }
        },
        axisTick: {
          show: false,
          aligWithLabel: true
        },
        axisLabel: {
          margin: 25,
          padding: 5,
          color: '#737373',
          fontFamily: 'Open Sans Regular',
          fontSize: 16,
          width: 40,
          height: 14,
          backgroundColor: 'rgba(100,100,100,0.03)',
          borderRadius: 6.44,
          shadowBlur: 4,
          shadowOffsetX: 0,
          shadowOffsetY: 2,
          shadowColor: 'rgba(0,0,0,0.1)',
          formatter: value => {
            return value.substring(0, 3).toUpperCase();
          }
        },
        triggerEvent: true
      },
      yAxis: [
        {
          type: 'value',
          position: 'left',
          axisLabel: {
            formatter: '{value} MWh',
            fontFamily: 'Open Sans Light',
            color: '#737373'
          },
          max: maxYaxis0
        },
        {
          type: 'value',
          position: 'right',
          axisLabel: {
            formatter: '{value} €/MWh',
            fontFamily: 'Open Sans Light',
            color: '#737373'
          },
          max: maxYaxis1
        }
      ],
      series: [
        {
          name: this.i18NextService.t('ANNUAL_SALES_CHART.CONSUMPTION'),
          type: 'line',
          lineStyle: {
            color: '#1A73E8'
          },
          areaStyle: {
            color: 'rgba(214,214,214,0.25)'
          },
          itemStyle: {
            color: '#1A73E8',
            width: 2
          },
          smooth: true,
          symbolSize: 8,
          data: data[0].months.map(item => item.datos.consumo)
        },
        {
          name: this.i18NextService.t('ANNUAL_SALES_CHART.MARGIN'),
          type: 'line',
          yAxisIndex: 1,
          lineStyle: {
            color: '#FF0000',
            width: 2
          },
          areaStyle: {
            color: 'rgba(230,48,39,0.25)'
          },
          itemStyle: {
            color: '#FF0000'
          },
          smooth: true,
          symbolSize: 8,
          data: data[0].months.map(item => item.datos.margen)
        },
        dataObjConsumption,
        dataObjMargin
      ]
    };
  }
}
